import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useAnalyticsUtilities } from '@air/analytics';
import { Workspaces } from '@air/api';
import { type WorkspaceListResponse } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { getWorkspacesListKey } from '~/swr-hooks/workspaces/useWorkspaces';

/** This hook returns a function which can be used to update a workspace (name, image, etc.) */
export const useUpdateWorkspace = () => {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useCurrentWorkspace();
  const { setGlobalEventProps } = useAnalyticsUtilities();

  const updateWorkspace = useMutation({
    mutationFn: (...params: Parameters<typeof Workspaces.update>) => Workspaces.update(...params),
    onMutate: async (...params) => {
      if (!currentWorkspace?.id) {
        return;
      }

      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: getWorkspacesListKey() });

      // Snapshot the previous value
      const previousWorkspaces = queryClient.getQueryData<WorkspaceListResponse>(getWorkspacesListKey());

      // Skip the optimistic write when the list cache is empty (e.g. admin.air.inc, where the list query is disabled) — writing `[]` flips `isFetched` on disabled observers and triggers the no-workspaces redirect.
      if (!previousWorkspaces) {
        return { previousWorkspaces };
      }

      queryClient.setQueryData(getWorkspacesListKey(), (workspaces?: WorkspaceListResponse) =>
        workspaces
          ? workspaces.map((ws) => (ws.id === currentWorkspace.id ? { ...ws, ...params[0] } : ws))
          : workspaces,
      );

      // Return a context object with the snapshotted value
      return { previousWorkspaces };
    },
    onSuccess: (remoteWorkspace) => {
      queryClient.invalidateQueries({ queryKey: getWorkspacesListKey() });

      setGlobalEventProps({ workspaceName: remoteWorkspace.name });
    },
    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: (error, params, context) => {
      reportErrorToBugsnag({
        error,
        context: 'Unable to update workspace',
        metadata: {
          key: 'Data',
          data: { params, workspaceId: currentWorkspace?.id },
        },
      });

      queryClient.setQueryData(getWorkspacesListKey(), context?.previousWorkspaces);
    },
  });

  return {
    updateWorkspace,
  };
};
